import {
  Badge,
  Button,
  Dropdown,
  Field,
  Input,
  MessageBar,
  MessageBarBody,
  makeStyles,
  Option,
  Spinner,
  Subtitle2,
  Switch,
  Tab,
  TabList,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  tokens,
} from "@fluentui/react-components"
import type { Branding, ThemeMode, UpdateBrandingRequest } from "@rema/shared/schemas/branding"
import type { ShardStrategy } from "@rema/shared/schemas/nodes"
import type { Settings, ShardSettings, UpdateSettingsRequest } from "@rema/shared/schemas/settings"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { type ReactNode, useEffect, useState } from "react"
import { PageHeader } from "../../components/page-header"
import { api, isApiError } from "../../lib/api"
import { problemMessage } from "../../lib/problem"
import { ApiAccessPanel } from "./api-access-panel"
import { settingsI18n as i18n } from "./i18n"
import { WebhooksPanel } from "./webhooks-panel"

const useStyles = makeStyles({
  panel: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    maxWidth: "560px",
    paddingTop: tokens.spacingVerticalL,
  },
  tabs: { marginBottom: tokens.spacingVerticalM },
  // El KID (key_…) es largo y sin espacios: que parta en vez de solaparse con Estado.
  kid: { fontFamily: tokens.fontFamilyMonospace, wordBreak: "break-all" },
  actions: { display: "flex", gap: tokens.spacingHorizontalS, alignItems: "center" },
  preview: { display: "flex", gap: tokens.spacingHorizontalM, marginTop: tokens.spacingVerticalS },
  swatch: {
    flex: 1,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalM,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
})

const SETTINGS_KEY = ["admin", "settings"] as const
const BRANDING_KEY = ["admin", "branding"] as const
const KEYS_KEY = ["admin", "keys"] as const

type TabValue = "branding" | "security" | "tokens" | "sharding" | "keys" | "apiAccess" | "webhooks"

function useSaveError() {
  const [error, setError] = useState<string | null>(null)
  const onError = (e: unknown) =>
    setError(isApiError(e) ? problemMessage(e.problem) : i18n.common.saveError)
  return { error, setError, onError }
}

const NumberField = ({
  label,
  value,
  onChange,
}: {
  label: string
  value: number
  onChange: (v: number) => void
}) => (
  <Field label={label}>
    <Input
      type="number"
      value={String(value)}
      onChange={(_, data) => onChange(Number(data.value))}
    />
  </Field>
)

const SaveBar = ({
  saving,
  error,
  onSave,
}: {
  saving: boolean
  error: string | null
  onSave: () => void
}) => {
  const styles = useStyles()
  return (
    <>
      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}
      <div className={styles.actions}>
        <Button
          appearance="primary"
          disabled={saving}
          icon={saving ? <Spinner size="tiny" /> : undefined}
          onClick={onSave}
        >
          {i18n.common.save}
        </Button>
      </div>
    </>
  )
}

// ---- Branding ----
const BrandingPanel = () => {
  const styles = useStyles()
  const qc = useQueryClient()
  const { error, setError, onError } = useSaveError()
  const query = useQuery({
    queryKey: BRANDING_KEY,
    queryFn: () => api.get<Branding>("/admin/branding"),
  })
  const [productName, setProductName] = useState("")
  const [primary, setPrimary] = useState("#0F6CBD")
  const [themeMode, setThemeMode] = useState<ThemeMode>("light")

  useEffect(() => {
    if (query.data) {
      setProductName(query.data.product_name)
      setPrimary(query.data.colors.primary)
      setThemeMode(query.data.theme_mode)
    }
  }, [query.data])

  const colorValid = /^#[0-9a-fA-F]{6}$/.test(primary)
  const save = useMutation({
    mutationFn: (input: UpdateBrandingRequest) => api.put<Branding>("/admin/branding", input),
    onSuccess: () => {
      setError(null)
      qc.invalidateQueries({ queryKey: BRANDING_KEY })
    },
    onError,
  })

  if (query.isPending) return <Spinner />
  return (
    <div className={styles.panel}>
      <Subtitle2>{i18n.branding.title}</Subtitle2>
      <Field label={i18n.branding.productName}>
        <Input value={productName} onChange={(_, d) => setProductName(d.value)} />
      </Field>
      <Field
        label={i18n.branding.color}
        validationState={colorValid ? "none" : "error"}
        validationMessage={colorValid ? undefined : i18n.branding.colorInvalid}
      >
        <Input value={primary} onChange={(_, d) => setPrimary(d.value)} />
      </Field>
      <Field label={i18n.branding.themeMode}>
        <Dropdown
          value={i18n.branding.themeModes[themeMode]}
          selectedOptions={[themeMode]}
          onOptionSelect={(_, d) => setThemeMode((d.optionValue as ThemeMode) ?? "light")}
        >
          <Option value="light">{i18n.branding.themeModes.light}</Option>
          <Option value="dark">{i18n.branding.themeModes.dark}</Option>
          <Option value="system">{i18n.branding.themeModes.system}</Option>
        </Dropdown>
      </Field>
      <div className={styles.preview}>
        <div className={styles.swatch} style={{ background: "#ffffff", color: "#242424" }}>
          <Badge style={colorValid ? { backgroundColor: primary } : undefined}>
            {i18n.branding.previewLight}
          </Badge>
        </div>
        <div className={styles.swatch} style={{ background: "#1f1f1f", color: "#ffffff" }}>
          <Badge style={colorValid ? { backgroundColor: primary } : undefined}>
            {i18n.branding.previewDark}
          </Badge>
        </div>
      </div>
      <SaveBar
        saving={save.isPending}
        error={error}
        onSave={() =>
          colorValid &&
          save.mutate({ product_name: productName, colors: { primary }, theme_mode: themeMode })
        }
      />
    </div>
  )
}

// ---- Settings (seguridad, tokens, sharding) ----
function useSettingsState() {
  const qc = useQueryClient()
  const { error, setError, onError } = useSaveError()
  const query = useQuery({
    queryKey: SETTINGS_KEY,
    queryFn: () => api.get<Settings>("/admin/settings"),
  })
  const save = useMutation({
    mutationFn: (input: UpdateSettingsRequest) => api.put<Settings>("/admin/settings", input),
    onSuccess: () => {
      setError(null)
      qc.invalidateQueries({ queryKey: SETTINGS_KEY })
    },
    onError,
  })
  return { query, save, error }
}

const SecurityPanel = () => {
  const styles = useStyles()
  const { query, save, error } = useSettingsState()
  const [minLength, setMinLength] = useState(12)
  const [maxLength, setMaxLength] = useState(128)
  const [maxAttempts, setMaxAttempts] = useState(5)
  const [baseLock, setBaseLock] = useState(60)
  const [maxLock, setMaxLock] = useState(3600)
  useEffect(() => {
    if (!query.data) return
    setMinLength(query.data.password_policy.min_length)
    setMaxLength(query.data.password_policy.max_length)
    setMaxAttempts(query.data.lockout_policy.max_attempts)
    setBaseLock(query.data.lockout_policy.base_lock_seconds)
    setMaxLock(query.data.lockout_policy.max_lock_seconds)
  }, [query.data])
  if (query.isPending) return <Spinner />
  return (
    <div className={styles.panel}>
      <Subtitle2>{i18n.security.title}</Subtitle2>
      <NumberField label={i18n.security.minLength} value={minLength} onChange={setMinLength} />
      <NumberField label={i18n.security.maxLength} value={maxLength} onChange={setMaxLength} />
      <NumberField
        label={i18n.security.maxAttempts}
        value={maxAttempts}
        onChange={setMaxAttempts}
      />
      <NumberField label={i18n.security.baseLock} value={baseLock} onChange={setBaseLock} />
      <NumberField label={i18n.security.maxLock} value={maxLock} onChange={setMaxLock} />
      <SaveBar
        saving={save.isPending}
        error={error}
        onSave={() =>
          save.mutate({
            password_policy: {
              min_length: minLength,
              max_length: maxLength,
              check_compromised: false,
            },
            lockout_policy: {
              max_attempts: maxAttempts,
              base_lock_seconds: baseLock,
              max_lock_seconds: maxLock,
            },
          })
        }
      />
    </div>
  )
}

const TokensPanel = () => {
  const styles = useStyles()
  const { query, save, error } = useSettingsState()
  const [ttls, setTtls] = useState<Settings["token_ttls"]>({
    access: 900,
    refresh: 2_592_000,
    m2m: 3600,
    mfa: 300,
    reset: 3600,
    invite: 604_800,
  })
  useEffect(() => {
    if (query.data) setTtls(query.data.token_ttls)
  }, [query.data])
  const set = (k: keyof Settings["token_ttls"]) => (v: number) => setTtls((p) => ({ ...p, [k]: v }))
  if (query.isPending) return <Spinner />
  return (
    <div className={styles.panel}>
      <Subtitle2>{i18n.tokens.title}</Subtitle2>
      <NumberField label={i18n.tokens.access} value={ttls.access} onChange={set("access")} />
      <NumberField label={i18n.tokens.refresh} value={ttls.refresh} onChange={set("refresh")} />
      <NumberField label={i18n.tokens.m2m} value={ttls.m2m} onChange={set("m2m")} />
      <NumberField label={i18n.tokens.mfa} value={ttls.mfa} onChange={set("mfa")} />
      <NumberField label={i18n.tokens.reset} value={ttls.reset} onChange={set("reset")} />
      <NumberField label={i18n.tokens.invite} value={ttls.invite} onChange={set("invite")} />
      <SaveBar
        saving={save.isPending}
        error={error}
        onSave={() => save.mutate({ token_ttls: ttls })}
      />
    </div>
  )
}

const ShardingPanel = () => {
  const styles = useStyles()
  const { query, save, error } = useSettingsState()
  const [strategy, setStrategy] = useState<ShardStrategy>("consistent-hash")
  const [autoReassign, setAutoReassign] = useState(false)
  useEffect(() => {
    if (!query.data) return
    setStrategy(query.data.shard_strategy.strategy)
    setAutoReassign(query.data.shard_strategy.auto_reassign)
  }, [query.data])
  if (query.isPending) return <Spinner />
  const strategies: ShardStrategy[] = ["consistent-hash", "least-loaded", "manual"]
  return (
    <div className={styles.panel}>
      <Subtitle2>{i18n.sharding.title}</Subtitle2>
      <Field label={i18n.sharding.strategy}>
        <Dropdown
          value={i18n.sharding.strategies[strategy]}
          selectedOptions={[strategy]}
          onOptionSelect={(_, d) =>
            setStrategy((d.optionValue as ShardStrategy) ?? "consistent-hash")
          }
        >
          {strategies.map((s) => (
            <Option key={s} value={s}>
              {i18n.sharding.strategies[s]}
            </Option>
          ))}
        </Dropdown>
      </Field>
      <Switch
        checked={autoReassign}
        onChange={(_, d) => setAutoReassign(d.checked)}
        label={i18n.sharding.autoReassign}
      />
      <SaveBar
        saving={save.isPending}
        error={error}
        onSave={() =>
          save.mutate({
            shard_strategy: { strategy, auto_reassign: autoReassign } as ShardSettings,
          })
        }
      />
    </div>
  )
}

// ---- Claves de firma ----
interface KeyInfo {
  kid: string
  alg: string
  status: "active" | "next" | "retired"
  created_at: string
}

const KeysPanel = () => {
  const styles = useStyles()
  const qc = useQueryClient()
  const { error, setError, onError } = useSaveError()
  const query = useQuery({
    queryKey: KEYS_KEY,
    queryFn: () => api.get<{ data: KeyInfo[] }>("/admin/keys"),
  })
  const rotate = useMutation({
    mutationFn: () => api.post<{ phase: string; kid: string }>("/admin/keys/rotate"),
    onSuccess: () => {
      setError(null)
      qc.invalidateQueries({ queryKey: KEYS_KEY })
    },
    onError,
  })
  if (query.isPending) return <Spinner />
  const keys = query.data?.data ?? []
  return (
    <div className={styles.panel}>
      <Subtitle2>{i18n.keys.title}</Subtitle2>
      <p>{i18n.keys.description}</p>
      {error ? (
        <MessageBar intent="error">
          <MessageBarBody>{error}</MessageBarBody>
        </MessageBar>
      ) : null}
      {keys.length === 0 ? (
        <MessageBar>
          <MessageBarBody>{i18n.keys.empty}</MessageBarBody>
        </MessageBar>
      ) : (
        <Table aria-label={i18n.keys.title}>
          <TableHeader>
            <TableRow>
              <TableHeaderCell>{i18n.keys.columns.kid}</TableHeaderCell>
              <TableHeaderCell>{i18n.keys.columns.status}</TableHeaderCell>
              <TableHeaderCell>{i18n.keys.columns.created}</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {keys.map((key) => (
              <TableRow key={key.kid}>
                <TableCell className={styles.kid}>{key.kid}</TableCell>
                <TableCell>
                  <Badge
                    appearance="filled"
                    color={
                      key.status === "active"
                        ? "success"
                        : key.status === "next"
                          ? "brand"
                          : "informative"
                    }
                  >
                    {i18n.keys.statuses[key.status]}
                  </Badge>
                </TableCell>
                <TableCell>{new Date(key.created_at).toLocaleString()}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
      <div className={styles.actions}>
        <Button
          appearance="primary"
          disabled={rotate.isPending}
          icon={rotate.isPending ? <Spinner size="tiny" /> : undefined}
          onClick={() => rotate.mutate()}
        >
          {i18n.keys.rotate}
        </Button>
      </div>
    </div>
  )
}

const PANELS: Record<TabValue, ReactNode> = {
  branding: <BrandingPanel />,
  security: <SecurityPanel />,
  tokens: <TokensPanel />,
  sharding: <ShardingPanel />,
  keys: <KeysPanel />,
  apiAccess: <ApiAccessPanel />,
  webhooks: <WebhooksPanel />,
}

export const SettingsPage = () => {
  const styles = useStyles()
  const [tab, setTab] = useState<TabValue>("branding")
  return (
    <>
      <PageHeader title={i18n.title} description={i18n.description} />
      <TabList
        className={styles.tabs}
        selectedValue={tab}
        onTabSelect={(_, d) => setTab(d.value as TabValue)}
      >
        <Tab value="branding">{i18n.tabs.branding}</Tab>
        <Tab value="security">{i18n.tabs.security}</Tab>
        <Tab value="tokens">{i18n.tabs.tokens}</Tab>
        <Tab value="sharding">{i18n.tabs.sharding}</Tab>
        <Tab value="keys">{i18n.tabs.keys}</Tab>
        <Tab value="apiAccess">{i18n.tabs.apiAccess}</Tab>
        <Tab value="webhooks">{i18n.tabs.webhooks}</Tab>
      </TabList>
      {PANELS[tab]}
    </>
  )
}
